// Progress for a video Bump AI is processing (workspace YouTube/local videos, Creator Studio).
//
// There is no true percentage while a video processes: chunks arrive in bursts and the last stretch
// (assembling the transcript, the AI pass) reports nothing. So the bar follows the clock instead:
//
//   • elapsed  — since the server stamped the job (`video_uploaded_time` on the content doc), so it
//                survives reloads and background tabs (whose timers the browser slows down)
//   • expected — this video's length × how long processing takes per minute of video for its source
//                (UsageStatistics/<source>VideosStatistics), never less than the fastest job on record
//
// It eases toward CAP (reaching ~80% at the expected time) and never claims to be done or moves
// backwards. The page swaps it out when the transcript lands.
//
// Chunk counters are real signals but not a proportion: a job is usually ~2 "chunks" — one audio
// part per 10 min of video (transcribed quickly, early) and the vision model's output (the slow part,
// landing at the very end; its intermediate files raise expected and received together). So audio
// parts may only carry the bar to AUDIO_SHARE, and "everything in" (only assembly left) raises it to
// ALL_IN.
//
// No jumps: counting parts made the old bar go 0 → 50 → 100. When the page opens the bar fills to the
// clock estimate (which is continuous, so a reload shows what you saw before); from then on chunk
// signals only raise a *target* the bar climbs toward at most MAX_RISE points a second, so
// "everything in" shows as a speed-up, never a leap.
import { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const CAP = 95;          // the bar never passes this before the job actually finishes
const AUDIO_SHARE = 15;  // audio parts are the quick, early part of a job
const ALL_IN = 88;       // every chunk in: only the transcript assembly is left
const MAX_RISE = 1;      // points per second the bar may climb once it's on screen
const STEEPNESS = 1.8;   // CAP × (1 − e^−1.8) ≈ 80% when elapsed = expected
const FALLBACK_MINUTES_PER_VIDEO_MINUTE = 0.4;
const FALLBACK_MINIMUM_MINUTES = 1;

export const BUMP_QUIPS = [
  'Bump AI is crunching frames like popcorn',
  'Bump AI is deep in binge-watch mode',
  'Bump AI spotted a highlight reel',
  'Bump AI decoding pixels at warp speed',
  'Bump AI is vibing with your video',
  'Bump AI is surfing video waves',
  'Bump AI in full video snack mode',
  'Bump AI finding golden nuggets in footage',
  'Bump AI diving into the pixels',
  'Bump AI analyzing clips with swag',
  'Bump AI is surfing the video stream',
  'Bump AI crunching pixels for breakfast',
  'Bump AI vibing through your video frames',
  'Bump AI just spotted something epic',
  'Bump AI getting cozy with this footage',
  'Bump AI catching video waves',
  'Bump AI on a pixel-powered mission',
  'Bump AI busy turning clips into magic',
  'Bump AI tuning into your highlights',
  'Bump AI making sense of moving pictures',
];

// One read per source per page load
const statsCache = {};
const loadStats = source => {
  if (!statsCache[source]) {
    statsCache[source] = getDoc(doc(db, 'UsageStatistics', `${source}VideosStatistics`))
      .then(snap => {
        const s = snap.exists() ? snap.data() : {};
        const perMinute = s.totalProcessingMinutes > 0 && s.totalVideoDurations > 0
          ? s.totalProcessingMinutes / s.totalVideoDurations
          : FALLBACK_MINUTES_PER_VIDEO_MINUTE;
        return { perMinute, minimumMinutes: Math.max(s.minimumProcessingMinutes || 0, FALLBACK_MINIMUM_MINUTES) };
      })
      .catch(() => ({ perMinute: FALLBACK_MINUTES_PER_VIDEO_MINUTE, minimumMinutes: FALLBACK_MINIMUM_MINUTES }));
  }
  return statsCache[source];
};

const toMillis = t => (t?.toMillis ? t.toMillis() : typeof t === 'number' ? t : null);

/**
 * @param {object} p
 * @param {boolean} p.active            the video is processing
 * @param {'local'|'youtube'} p.source  which statistics to estimate from
 * @param {string} p.id                 content id (remembers when this browser first saw an unstamped job)
 * @param {object} p.job                the content doc's data (video_uploaded_time, video_duration_in_seconds,
 *                                      and the chunk counters), or null until it arrives
 * @returns {number} 0–95
 */
export default function useProcessingProgress({ active, source, id, job }) {
  const [percent, setPercent] = useState(0); // kept fractional; shown rounded
  const shownRef = useRef(false);            // first reading done (after it, no jumps)
  const [stats, setStats] = useState(null);
  const jobRef = useRef(job);
  jobRef.current = job;

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    loadStats(source).then(s => { if (live) setStats(s); });
    return () => { live = false; };
  }, [active, source]);

  useEffect(() => {
    const seenKey = `processingSeen_${id}`;
    if (!active) {
      setPercent(0);
      shownRef.current = false;
      try { localStorage.removeItem(seenKey); } catch {}
      return undefined;
    }
    // Nothing until both the job and the statistics are in: the bar never moves back, so an early
    // guess (say, from the fallback rate) would stick
    if (!stats) return undefined;

    // Jobs without a server stamp count from when this browser first saw them processing
    const seenAt = () => {
      try {
        const saved = Number(localStorage.getItem(seenKey));
        if (saved) return saved;
        localStorage.setItem(seenKey, String(Date.now()));
      } catch {}
      return Date.now();
    };

    const tick = () => {
      const j = jobRef.current;
      if (!j) return;
      const startedAt = toMillis(j.video_uploaded_time) || seenAt();
      const elapsed = Math.max(0, Date.now() - startedAt) / 1000;
      const videoMinutes = (j.video_duration_in_seconds || 0) / 60;
      const expected = Math.max(stats.minimumMinutes, videoMinutes * stats.perMinute) * 60;
      const byTime = CAP * (1 - Math.exp(-STEEPNESS * (elapsed / expected)));

      const audioTotal = j.total_audio_chunks || 0;
      const audioIn = Math.min(j.received_audio_chunks || 0, audioTotal);
      const videoIn = (j.received_video_chunks || 0) > 0 && (j.received_video_chunks || 0) >= (j.expected_video_chunks || 0);
      const byChunks = audioIn === audioTotal && videoIn ? ALL_IN
        : audioTotal > 0 ? (audioIn / audioTotal) * AUDIO_SHARE
        : 0;

      const target = Math.min(CAP, Math.max(byTime, byChunks));
      setPercent(prev => {
        if (!shownRef.current) { shownRef.current = true; return Math.max(prev, byTime); }
        return Math.max(prev, Math.min(target, prev + MAX_RISE));
      });
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [active, id, stats]);

  return Math.round(percent);
}
